import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { ReportingUserService } from '@http/clients/reporting-app/reporting-user.service';
import { CustomMenuItemComponent } from '@shared/custom-menu-item/custom-menu-item.component';
import { GenericTypeAheadMultiselectComponent } from 'ngx-lib';
import { FormEditorBase, createValueAccessorProvider } from '../../base/form-editor-base';

@Component({
    selector: 'app-report-user-form-editor',
    templateUrl: './report-user-form-editor.component.html',
    styleUrls: ['./report-user-form-editor.component.scss'],
    providers: [createValueAccessorProvider(ReportUserFormEditorComponent)],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [GenericTypeAheadMultiselectComponent, CustomMenuItemComponent]
})
export class ReportUserFormEditorComponent extends FormEditorBase<number[] | null> {
    readonly = input<boolean>(false);
    labelText = input<string>('Salespersons');
    placeholderText = input<string>('Search Users');
    hasLabel = input<boolean>(true);
    private suppressSyncEmission = false;
    private lastSyncedIdsKey = '';
    private syncRequestId = 0;

    private readonly reportingUserService = inject(ReportingUserService);

    override writeValue(value: number[] | null): void {
        this.selectedValue.set(value ?? undefined);
        void this.syncSelectedUsers(value);
    }

    protected override async onEntityTextChange(text: string): Promise<void> {
        if (!text?.trim()) {
            this.items.set([]);
            return;
        }
        const users = await this.reportingUserService.search(text);
        this.items.set(users);
    }

    protected override mapSelectedItemsToValue(): number[] | null {
        if (this.suppressSyncEmission) {
            this.suppressSyncEmission = false;
            return (this.selectedValue() as number[] | undefined) ?? null;
        }
        const selected = this.selectedItems() ?? [];
        if (!selected.length) return null;
        return selected.map((item) => Number(item.id));
    }

    protected override mapValueToSelectedItems(): any[] {
        return this.selectedItems() ?? [];
    }

    private async syncSelectedUsers(value: number[] | null): Promise<void> {
        const normalizedIds = (value ?? []).map((id) => Number(id)).filter((id) => Number.isFinite(id));
        const idsKey = [...normalizedIds].sort((a, b) => a - b).join(',');

        if (idsKey === this.lastSyncedIdsKey) return;
        this.lastSyncedIdsKey = idsKey;

        if (!normalizedIds.length) {
            this.suppressSyncEmission = true;
            this.selectedItems.set([]);
            return;
        }

        const requestId = ++this.syncRequestId;
        const selectedUsers = await this.reportingUserService.getByIds(normalizedIds);
        if (requestId !== this.syncRequestId) return;

        this.suppressSyncEmission = true;
        this.selectedItems.set(selectedUsers);
    }
}
